import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { Check, AlertTriangle, Loader2 } from 'lucide-react'
import type { ChatMessage, Approval } from '../../../shared/types/chat'
import { getRiskColor } from '../utils'
import { BottomSheet } from '@tendbot/ui'
import * as api from '../api'

interface PendingItem {
  messageId: string
  approval: Approval
}

interface BatchApprovalSheetProps {
  open: boolean
  messages: ChatMessage[]
  onClose: () => void
  onApproved: (
    approvedMessageIds: string[],
    reply?: { messageId: string; content: string; timestamp: string },
  ) => void
  onSingleApprove: (messageId: string) => void
}

export default function BatchApprovalSheet({
  open,
  messages,
  onClose,
  onApproved,
  onSingleApprove,
}: BatchApprovalSheetProps) {
  const { t } = useTranslation('chat')
  const [skippedIds, setSkippedIds] = useState<Set<string>>(new Set())
  const [processing, setProcessing] = useState(false)

  const pendingItems = useMemo<PendingItem[]>(() =>
    messages
      .filter((m): m is ChatMessage & { approval: Approval } =>
        m.approval?.status === 'pending' && !skippedIds.has(m.id),
      )
      .map(m => ({ messageId: m.id, approval: m.approval })),
  [messages, skippedIds])

  const handleSkip = useCallback((messageId: string) => {
    setSkippedIds(prev => new Set(prev).add(messageId))
  }, [])

  const handleApproveAll = useCallback(async () => {
    if (pendingItems.length === 0) return
    setProcessing(true)
    try {
      const approvalIds = pendingItems.map(item => item.approval.id)
      const response = await api.batchApproveActions(approvalIds)
      const approvedMessageIds = pendingItems
        .filter(item => {
          const result = response.results.find(r => r.approvalId === item.approval.id)
          return result?.outcome === 'approved'
        })
        .map(item => item.messageId)
      onApproved(approvedMessageIds, response.reply)
      onClose()
    } catch (err) {
      console.error('Batch approve failed:', err)
    } finally {
      setProcessing(false)
    }
  }, [pendingItems, onApproved, onClose])

  const handleSingleApprove = useCallback((messageId: string) => {
    onSingleApprove(messageId)
    setSkippedIds(prev => new Set(prev).add(messageId))
  }, [onSingleApprove])

  // Reset skipped when sheet closes
  useEffect(() => {
    if (open) setSkippedIds(new Set())
  }, [open])

  return (
    <BottomSheet
      open={open}
      onOpenChange={(o) => { if (!o && !processing) onClose() }}
      title={t('approval.batch.title')}
      swipeEnabled={!processing}
      className="max-h-[85vh]"
    >
      <div className="px-5 pb-3 border-b border-white/5">
        <h3 className="text-ivory font-semibold text-base">{t('approval.batch.title')}</h3>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
        {pendingItems.length === 0 ? (
          <p className="text-pearl/60 text-sm text-center py-8">
            {t('approval.batch.done', { count: 0 })}
          </p>
        ) : (
          <AnimatePresence initial={false}>
            {pendingItems.map((item, index) => (
              <motion.div
                key={item.messageId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ delay: index * 0.03 }}
              >
                <BatchItem
                  item={item}
                  onApprove={() => handleSingleApprove(item.messageId)}
                  onSkip={() => handleSkip(item.messageId)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {pendingItems.length > 0 && (
        <div className="px-5 py-4 border-t border-white/5">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleApproveAll}
            disabled={processing}
            className="w-full py-3 rounded-xl bg-gold text-midnight font-semibold text-sm flex items-center justify-center gap-2 hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <>
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Loader2 size={16} />
                </motion.span>
                {t('approval.batch.processing', { count: pendingItems.length })}
              </>
            ) : (
              <>
                <Check size={16} />
                {t('approval.batch.approveAll')} ({pendingItems.length})
              </>
            )}
          </motion.button>
        </div>
      )}
    </BottomSheet>
  )
}

// ─── Compact item card for the triage list ───────────────────

function BatchItem({
  item,
  onApprove,
  onSkip,
}: {
  item: PendingItem
  onApprove: () => void
  onSkip: () => void
}) {
  const { t } = useTranslation('chat')
  const { approval } = item
  const [processing, setProcessing] = useState(false)

  const handleApprove = () => {
    setProcessing(true)
    onApprove()
  }

  const emailTo = approval.extendedInfo?.email?.to
  const emailSubject = approval.extendedInfo?.email?.subject
  const eventTitle = approval.extendedInfo?.event?.title

  return (
    <div className={`glass-card rounded-xl overflow-hidden border-l-4 ${getRiskColor(approval.risk)} p-3`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${getRiskColor(approval.risk)}`}>
              {approval.risk === 'high' && <AlertTriangle size={9} className="inline mr-0.5" />}
              {approval.risk}
            </span>
            {approval.extendedInfo?.service && (
              <span className="text-[10px] text-pearl/55">{approval.extendedInfo.service}</span>
            )}
          </div>
          <p className="text-ivory text-sm font-medium truncate">{approval.title}</p>
          {emailTo && emailSubject && (
            <p className="text-pearl/50 text-xs truncate mt-0.5">
              {emailTo} — {emailSubject}
            </p>
          )}
          {eventTitle && !emailTo && (
            <p className="text-pearl/50 text-xs truncate mt-0.5">{eventTitle}</p>
          )}
          {!emailTo && !eventTitle && approval.details && (
            <p className="text-pearl/50 text-xs truncate mt-0.5">{approval.details}</p>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={onSkip}
            className="px-2.5 py-1.5 rounded-lg text-pearl/60 text-xs hover:text-pearl/70 hover:bg-white/5 transition-colors"
          >
            {t('approval.batch.skip')}
          </button>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handleApprove}
            disabled={processing}
            className="w-9 h-9 rounded-full bg-gold/90 text-midnight flex items-center justify-center hover:bg-gold transition-colors disabled:opacity-50"
          >
            {processing ? (
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Loader2 size={14} />
              </motion.span>
            ) : (
              <Check size={16} strokeWidth={2.5} />
            )}
          </motion.button>
        </div>
      </div>
    </div>
  )
}
